/**
 * Flow Engine for processing builder-generated JSON flow structure
 * Handles state transitions, message sequencing, and field collection
 */

import type { Message, RegularMessage, OptionsMessage, ActionBlock, ImageMessage, IframeBlock, PhotoUploadMessage } from '../types/index';
import type { BuilderFlow, BuilderState, BuilderMessage, ChatSession, UploadedAttachment } from '../types/builder';

export class FlowEngine {
    private flow: BuilderFlow;
    private currentStateId: string;
    private collectedFields: Record<string, any>;
    private chatHistory: Message[];
    private attachments: UploadedAttachment[] = [];
    private sessionId: string;
    private aiEnabled: boolean;

    // AI conversation mode state
    private aiMode: boolean = false;
    private aiConversationHistory: Array<{ role: 'user' | 'assistant'; content: string; timestamp: Date }> = [];
    private aiTurnCount: number = 0;
    private aiConfig: BuilderState['aiConfig'] | null = null;

    constructor(flow: BuilderFlow, sessionId?: string, aiEnabled: boolean = true) {
        this.flow = flow;
        this.currentStateId = 'welcome';
        this.collectedFields = {};
        this.chatHistory = [];
        this.aiEnabled = aiEnabled;
        // Use provided sessionId or generate new one
        this.sessionId = sessionId || `session-${Date.now()}`;
    }

    /**
     * Initialize flow by loading welcome state messages
     */
    async initialize(): Promise<Message[]> {
        const welcomeState = this.flow.welcome;
        if (!welcomeState) {
            throw new Error('Welcome state not found in flow');
        }

        const messages = this.processState(welcomeState);

        // Store messages in history
        messages.forEach(msg => this.chatHistory.push(msg));

        return messages;
    }

    /**
     * Check if current state should auto-advance to next state
     * Auto-advance when:
     * - State has next_state_on_success
     * - State does NOT have field_to_collect
     * - State does NOT have options message
     */
    shouldAutoAdvance(): boolean {
        // Never auto-advance in AI conversation mode
        if (this.aiMode) return false;

        const currentState = this.getCurrentState();
        if (!currentState) return false;

        // Never auto-advance an AI conversation state (belt-and-suspenders with aiMode check)
        if (currentState.type === 'ai_conversation') return false;

        // Must have next_state_on_success to advance
        if (!currentState.next_state_on_success) return false;

        // Don't auto-advance if collecting fields
        if (currentState.field_to_collect) return false;

        // Don't auto-advance if has options (user must select)
        if (this.hasOptionsMessage(currentState)) return false;

        // Don't auto-advance past a photo upload (user must press Continue)
        if (this.hasPhotoUpload(currentState)) return false;

        return true;
    }

    /**
     * Auto-advance to next state (for states without user interaction)
     */
    async autoAdvance(): Promise<{
        messages: Message[];
        shouldWaitForInput: boolean;
        hasReachedEnd: boolean;
    }> {
        const currentState = this.getCurrentState();
        if (!currentState || !currentState.next_state_on_success) {
            return {
                messages: [],
                shouldWaitForInput: false,
                hasReachedEnd: this.hasReachedEnd(),
            };
        }

        // Transition to next state
        this.currentStateId = currentState.next_state_on_success;
        const nextState = this.flow[this.currentStateId];

        if (!nextState) {
            console.error('Next state not found:', this.currentStateId);
            return {
                messages: [],
                shouldWaitForInput: false,
                hasReachedEnd: true,
            };
        }

        // Process next state messages
        const messages = this.processState(nextState);

        // Store messages in history
        messages.forEach(msg => this.chatHistory.push(msg));

        // Determine if we should wait for input
        const shouldWaitForInput = this.shouldWaitForInputInState(nextState);

        return {
            messages,
            shouldWaitForInput,
            hasReachedEnd: this.hasReachedEnd(),
        };
    }

    /**
     * Process a state and return its messages
     */
    private processState(state: BuilderState): Message[] {
        const messages: Message[] = [];

        for (const builderMessage of state.messages) {
            const message = this.convertBuilderMessageToMessage(builderMessage, state.id);
            if (message) {
                messages.push(message);
            }
        }

        // If this is an AI conversation state, enter AI mode after displaying greeting messages
        if (state.type === 'ai_conversation') {
            if (!this.aiEnabled) {
                // AI is disabled — skip this node
                console.log('[FlowEngine] AI disabled, skipping AI state:', state.id);
                if (state.next_state_on_success) {
                    // Auto-transition to next state
                    const nextState = this.flow[state.next_state_on_success];
                    if (nextState) {
                        this.currentStateId = state.next_state_on_success;
                        const nextMessages = this.processState(nextState);
                        messages.push(...nextMessages);
                    }
                } else {
                    // No next state — show a fallback message
                    const fallback: RegularMessage = {
                        type: 'regular',
                        text: "I'd love to help! Please contact our team directly for assistance.",
                        sender: 'bot',
                        timestamp: new Date(),
                        flow_state: state.id,
                    };
                    messages.push(fallback);
                }
            } else {
                console.log('[FlowEngine] AI conversation state detected:', state.id, '- entering AI mode');
                this.enterAIMode(state);
            }
        }

        return messages;
    }

    /**
     * Convert builder message format to widget message format
     */
    private convertBuilderMessageToMessage(
        builderMessage: BuilderMessage,
        stateId: string
    ): Message | null {
        const timestamp = new Date();

        switch (builderMessage.type) {
            case 'regular': {
                const msg: RegularMessage = {
                    type: 'regular',
                    text: builderMessage.data?.text || '',
                    sender: 'bot',
                    timestamp,
                    flow_state: stateId,
                };
                return msg;
            }

            case 'options': {
                const options = builderMessage.data?.options?.map((opt: any) => ({
                    text: opt.text,
                    value: opt.value || opt.text,
                    icon: opt.icon, // Lucide icon name
                    image: opt.image, // Include image field
                    link: opt.link, // Include link for redirects
                    redirectDelayMs: opt.redirectDelayMs, // Include redirect delay
                    nextStateId: opt.nextStateId, // Store for navigation
                    customStyle: opt.customStyle, // Per-option styling
                })) || [];

                console.log('[FlowEngine] Processing options message:', {
                    text: builderMessage.data?.text,
                    optionsCount: options.length,
                    options: options.map(o => ({ text: o.text, hasImage: !!o.image, hasLink: !!o.link }))
                });

                const msg: OptionsMessage = {
                    type: 'options',
                    text: builderMessage.data?.text || '',
                    options,
                    showPerRow: builderMessage.data?.showPerRow || 1,
                    displayStyle: builderMessage.data?.displayStyle || 'cards',
                    showBackground: builderMessage.data?.showBackground,
                    sender: 'bot',
                    timestamp,
                    flow_state: stateId,
                };
                return msg;
            }

            case 'action_block': {
                const buttons = builderMessage.data?.buttons?.map((btn: any) => ({
                    text: btn.text,
                    icon: btn.icon,
                    url: btn.url,
                    customStyle: btn.customStyle,
                })) || [];

                const msg: ActionBlock = {
                    type: 'action_block',
                    title: builderMessage.data?.title,
                    buttons,
                    showPerRow: builderMessage.data?.showPerRow || 3,
                    showBackground: builderMessage.data?.showBackground,
                    sender: 'bot',
                    timestamp,
                };
                return msg;
            }

            case 'image_message': {
                const blocks = builderMessage.data?.blocks?.map((block: any) => {
                    if (block.type === 'text') {
                        return {
                            type: 'text' as const,
                            content: block.content || '',
                        };
                    } else if (block.type === 'image') {
                        return {
                            type: 'image' as const,
                            images: block.images?.map((img: any) => ({
                                url: img.url,
                                alt: img.alt,
                            })) || [],
                            showPerRow: block.showPerRow || 1,
                            scale: block.scale || 1,
                            carousel: block.carousel || false,
                            carouselDelay: block.carouselDelay || 4,
                        };
                    }
                    return null;
                }).filter(Boolean) || [];

                const msg: ImageMessage = {
                    type: 'image_message',
                    blocks: blocks as any, // Type assertion needed due to complex union type
                    sender: 'bot',
                    timestamp,
                };
                return msg;
            }

            case 'photo_upload': {
                const msg: PhotoUploadMessage = {
                    type: 'photo_upload',
                    text: builderMessage.data?.text || 'Please upload your photo',
                    hint: builderMessage.data?.hint,
                    buttonLabel: builderMessage.data?.buttonLabel,
                    sender: 'bot',
                    timestamp,
                    flow_state: stateId,
                };
                return msg;
            }

            case 'iframe_block': {
                const embedCode = builderMessage.data?.embedCode || '';
                // Parse src and height from the raw embed code
                const srcMatch = embedCode.match(/src=["']([^"']+)["']/i);
                const parsedSrc = srcMatch ? srcMatch[1] : '';
                const heightMatch = embedCode.match(/height=["']?(\d+)["']?/i);
                const parsedHeight = heightMatch ? parseInt(heightMatch[1], 10) : 315;

                const msg: IframeBlock = {
                    type: 'iframe_block',
                    url: parsedSrc,
                    title: builderMessage.data?.title,
                    height: builderMessage.data?.height || parsedHeight,
                    allowFullscreen: builderMessage.data?.allowFullscreen ?? true,
                    sender: 'bot',
                    timestamp,
                };
                return msg;
            }

            default:
                console.warn('Unknown message type:', builderMessage.type);
                return null;
        }
    }

    /**
     * Handle user input/selection and transition to next state
     */
    async handleUserInput(input: string, selectedOption?: any): Promise<{
        messages: Message[];
        shouldWaitForInput: boolean;
        hasReachedEnd: boolean;
    }> {
        const currentState = this.getCurrentState();
        if (!currentState) {
            throw new Error('Current state not found');
        }

        // Add user message to history
        const userMessage: RegularMessage = {
            type: 'regular',
            text: input,
            sender: 'user',
            timestamp: new Date(),
            flow_state: currentState.id,
        };
        this.chatHistory.push(userMessage);

        // If current state has field_to_collect, store it
        if (currentState.field_to_collect) {
            this.collectedFields[currentState.field_to_collect] = input;
        }

        // Determine next state
        let nextStateId: string | undefined;

        if (selectedOption && selectedOption.nextStateId) {
            // Option was selected, use its nextStateId
            nextStateId = selectedOption.nextStateId;
        } else if (currentState.next_state_on_success) {
            // Use the state's next_state_on_success
            nextStateId = currentState.next_state_on_success;
        }

        if (!nextStateId) {
            // End of flow - no next state to go to
            return {
                messages: [],
                shouldWaitForInput: false,
                hasReachedEnd: this.hasReachedEnd(),
            };
        }

        // Transition to next state
        this.currentStateId = nextStateId;
        const nextState = this.flow[nextStateId];

        if (!nextState) {
            console.error('Next state not found:', nextStateId);
            return {
                messages: [],
                shouldWaitForInput: false,
                hasReachedEnd: true,
            };
        }

        // Process next state messages
        const messages = this.processState(nextState);

        // Store messages in history
        messages.forEach(msg => this.chatHistory.push(msg));

        // Determine if we should wait for input
        const shouldWaitForInput = this.shouldWaitForInputInState(nextState);

        return {
            messages,
            shouldWaitForInput,
            hasReachedEnd: this.hasReachedEnd(),
        };
    }

    /**
     * Check if state has options message (no next_state_on_success)
     */
    private hasOptionsMessage(state: BuilderState): boolean {
        return state.messages.some((msg: BuilderMessage) => msg.type === 'options');
    }

    private hasPhotoUpload(state: BuilderState): boolean {
        return state.messages.some((msg: BuilderMessage) => msg.type === 'photo_upload');
    }

    /** True while the current state is waiting for the visitor's photo. */
    isAwaitingPhoto(): boolean {
        const currentState = this.getCurrentState();
        return !this.aiMode && !!currentState && this.hasPhotoUpload(currentState);
    }

    addAttachment(attachment: UploadedAttachment): void {
        this.attachments = [
            ...this.attachments.filter(a => a.stateId !== attachment.stateId),
            attachment,
        ];
    }

    getAttachments(): UploadedAttachment[] {
        return this.attachments.map(({ file, ext, token, stateId }) => ({ file, ext, token, stateId }));
    }

    /**
     * Determine if we should wait for user input in this state
     */
    private shouldWaitForInputInState(state: BuilderState): boolean {
        // AI conversation states always wait for user input
        if (state.type === 'ai_conversation') {
            return true;
        }

        // If state has field_to_collect, wait for input
        if (state.field_to_collect) {
            return true;
        }

        // If state has options message, don't wait for text input (use options instead)
        if (this.hasOptionsMessage(state)) {
            return false;
        }

        // Default: don't wait
        return false;
    }

    /**
     * Check meta flag to see if this state should be treated as a lead-submitting end state.
     * For legacy templates, undefined is treated as true.
     */
    private isEndStateMeta(state: BuilderState): boolean {
        if (typeof state.is_end_state === 'boolean') {
            return state.is_end_state;
        }
        return true;
    }

    /**
     * Check if current state has options
     */
    hasOptions(): boolean {
        const currentState = this.getCurrentState();
        return currentState ? this.hasOptionsMessage(currentState) : false;
    }

    /**
     * Check if flow has reached the end
     * End is reached when current state has no next steps and no user interaction needed
     */
    hasReachedEnd(): boolean {
        // Never at end while in AI conversation mode
        if (this.aiMode) return false;

        const currentState = this.getCurrentState();
        if (!currentState) return true;

        // AI conversation state is never an end state
        if (currentState.type === 'ai_conversation') return false;

        // If has next_state_on_success, not at end
        if (currentState.next_state_on_success) return false;

        // If collecting field, not at end (waiting for input)
        if (currentState.field_to_collect) return false;

        // If has options, not at end (waiting for selection)
        if (this.hasOptionsMessage(currentState)) return false;

        // Photo upload is not at end until the visitor presses Continue
        if (this.hasPhotoUpload(currentState) && !this.attachments.some(a => a.stateId === currentState.id)) return false;

        // No next steps and no interaction needed → structurally at end.
        // Only treat as lead-submitting end if meta flag allows it.
        if (!this.isEndStateMeta(currentState)) return false;

        return true;
    }

    /**
     * Get current state
     */
    getCurrentState(): BuilderState | null {
        return this.flow[this.currentStateId] || null;
    }

    /**
     * Get collected fields data
     */
    getCollectedFields(): Record<string, any> {
        return { ...this.collectedFields };
    }

    /**
     * Get chat history
     */
    getChatHistory(): Message[] {
        return [...this.chatHistory];
    }

    /**
     * Get session data for submission
     */
    getSessionData(): ChatSession {
        // Merge AI conversation into chatHistory at the correct position
        const history = this.getChatHistory();

        if (this.aiConversationHistory.length > 0) {
            // Find the AI conversation state ID
            const aiStateId = Object.keys(this.flow).find(
                id => this.flow[id].type === 'ai_conversation'
            );

            if (aiStateId) {
                // Find the insertion point: right after the last message that belongs to the AI state
                // (this is typically the greeting message)
                let insertIdx = history.length;
                for (let i = 0; i < history.length; i++) {
                    const msg = history[i] as any;
                    // Find first message AFTER the AI state block
                    if (msg.flow_state && msg.flow_state !== aiStateId) {
                        // Check if there was an AI state message before this
                        const hadAiStateBefore = history.slice(0, i).some(
                            (m: any) => m.flow_state === aiStateId
                        );
                        if (hadAiStateBefore) {
                            insertIdx = i;
                            break;
                        }
                    }
                }

                // If no post-AI messages found, insert after last AI state message
                if (insertIdx === history.length) {
                    for (let i = history.length - 1; i >= 0; i--) {
                        const msg = history[i] as any;
                        if (msg.flow_state === aiStateId) {
                            insertIdx = i + 1;
                            break;
                        }
                    }
                }

                // Build AI conversation messages with correct timestamps
                const aiMessages: Message[] = this.aiConversationHistory.map(m => ({
                    type: 'regular' as const,
                    text: m.content,
                    sender: m.role === 'user' ? 'user' as const : 'bot' as const,
                    timestamp: m.timestamp,
                    flow_state: aiStateId,
                }));

                // Deduplicate against existing messages
                const existingTexts = new Set(
                    history.map(m => `${m.sender}:${m.type === 'regular' ? (m as any).text : ''}`)
                );
                const newAiMessages = aiMessages.filter(
                    m => !existingTexts.has(`${m.sender}:${(m as any).text}`)
                );

                // Insert at the correct position
                history.splice(insertIdx, 0, ...newAiMessages);
            }
        }

        return {
            sessionId: this.sessionId,
            chatHistory: history,
            collectedFields: this.getCollectedFields(),
            currentState: this.currentStateId,
            timestamp: new Date(),
        };
    }

    /**
     * Check if input should be enabled
     * Enabled when: State has field_to_collect
     * Disabled when: Options message is showing or no interaction needed
     */
    shouldEnableInput(): boolean {
        // In AI mode, input is always enabled
        if (this.aiMode) return true;

        const currentState = this.getCurrentState();
        if (!currentState) return false;

        // AI conversation state — input always enabled
        if (currentState.type === 'ai_conversation') return true;

        // If has options message, disable input (user should click options)
        if (this.hasOptionsMessage(currentState)) {
            return false;
        }

        // If state expects field collection, enable input
        if (currentState.field_to_collect) {
            return true;
        }

        // Default: disable
        return false;
    }

    /**
     * Restore engine state to a specific point based on message history
     * Used for undo functionality to rebuild state from truncated messages
     */
    restoreToPoint(messages: Message[]): void {
        // Reset state
        this.collectedFields = {};
        this.chatHistory = [...messages];

        // Reset AI mode state
        this.aiMode = false;
        this.aiConversationHistory = [];
        this.aiTurnCount = 0;
        this.aiConfig = null;

        // Find the last flow state from messages
        let lastFlowState = 'welcome';
        const collectedFieldsMap: Record<string, any> = {};

        // Replay messages to rebuild state
        for (const message of messages) {
            // Track flow state changes (only RegularMessage and OptionsMessage have flow_state)
            if ((message.type === 'regular' || message.type === 'welcome' || message.type === 'system' || message.type === 'options' || message.type === 'photo_upload') && 'flow_state' in message && message.flow_state) {
                lastFlowState = message.flow_state;
            }

            // Rebuild collected fields from user messages
            if (message.sender === 'user' && message.type === 'regular') {
                const regularMsg = message as RegularMessage;

                // Find which field this message was collecting
                // Look for the state it was sent in
                if (regularMsg.flow_state) {
                    const state = this.flow[regularMsg.flow_state];
                    if (state && state.field_to_collect) {
                        collectedFieldsMap[state.field_to_collect] = regularMsg.text;
                    }
                }
            }
        }

        this.currentStateId = lastFlowState;
        this.collectedFields = collectedFieldsMap;

        // A photo counts only while the visitor's reply after its upload card is
        // still in the history; undoing past it means they will upload again.
        this.attachments = this.attachments.filter(att => {
            let cardIdx = -1;
            messages.forEach((m, i) => {
                if (m.type === 'photo_upload' && m.flow_state === att.stateId) cardIdx = i;
            });
            return cardIdx >= 0 && messages.slice(cardIdx + 1).some(m => m.sender === 'user');
        });

        // If the restored state is an AI conversation state, re-enter AI mode
        // and rebuild conversation history from the messages
        const restoredState = this.flow[lastFlowState];
        if (restoredState && restoredState.type === 'ai_conversation') {
            this.enterAIMode(restoredState);

            // Rebuild AI conversation history from messages in this state
            const aiMessages = messages.filter(m =>
                'flow_state' in m && m.flow_state === lastFlowState
            );

            const history: Array<{ role: 'user' | 'assistant'; content: string; timestamp: Date }> = [];
            for (const msg of aiMessages) {
                if (msg.sender === 'user' && msg.type === 'regular') {
                    history.push({ role: 'user', content: (msg as RegularMessage).text, timestamp: msg.timestamp });
                } else if (msg.sender === 'bot' && msg.type === 'regular') {
                    // Skip the initial greeting messages (they appear before any user message)
                    if (history.length > 0 || aiMessages.some(m => m.sender === 'user')) {
                        const hasUserBefore = aiMessages.indexOf(msg) > aiMessages.findIndex(m => m.sender === 'user');
                        if (hasUserBefore) {
                            history.push({ role: 'assistant', content: (msg as RegularMessage).text, timestamp: msg.timestamp });
                        }
                    }
                }
            }

            this.aiConversationHistory = history;
            this.aiTurnCount = history.filter(h => h.role === 'user').length;
        }

        console.log('[FlowEngine] State restored to point:', {
            messagesCount: messages.length,
            currentStateId: this.currentStateId,
            collectedFields: this.collectedFields,
            aiMode: this.aiMode,
            aiTurnCount: this.aiTurnCount,
        });
    }

    // ─── AI Conversation Mode Methods ────────────────────────────────

    /**
     * Check if engine is currently in AI conversation mode
     */
    isInAIConversation(): boolean {
        return this.aiMode;
    }

    /**
     * Get AI configuration for the current AI state
     */
    getAIConfig(): BuilderState['aiConfig'] | null {
        return this.aiConfig;
    }

    /**
     * Get AI conversation history for context
     */
    getAIConversationHistory(): Array<{ role: 'user' | 'assistant'; content: string }> {
        return this.aiConversationHistory.map(({ role, content }) => ({ role, content }));
    }

    /**
     * Get current AI turn count
     */
    getAITurnCount(): number {
        return this.aiTurnCount;
    }

    /**
     * Enter AI conversation mode for a given state
     */
    private enterAIMode(state: BuilderState): void {
        this.aiMode = true;
        this.aiConfig = state.aiConfig || null;
        this.aiConversationHistory = [];
        this.aiTurnCount = 0;
        console.log('[FlowEngine] Entered AI conversation mode:', {
            stateId: state.id,
            businessName: this.aiConfig?.business_name,
        });
    }

    /**
     * Record an AI exchange (user message + assistant response)
     */
    addAIExchange(userMessage: string, assistantMessage: string): void {
        const now = new Date();
        this.aiConversationHistory.push(
            { role: 'user', content: userMessage, timestamp: now },
            { role: 'assistant', content: assistantMessage, timestamp: new Date(now.getTime() + 1) }
        );
        this.aiTurnCount++;
    }

    /**
     * Exit AI mode and return the next state ID for lead capture handoff
     */
    exitAIMode(): string | undefined {
        const currentState = this.getCurrentState();
        this.aiMode = false;
        this.aiTurnCount = 0;
        this.aiConfig = null;
        console.log('[FlowEngine] Exited AI conversation mode');
        return currentState?.next_state_on_success;
    }

    /**
     * Get the configured lead capture turn threshold for the current AI state.
     * Falls back to 5 if not configured.
     */
    getLeadCaptureThreshold(): number {
        return this.aiConfig?.lead_capture_turn_threshold ?? 5;
    }

    /**
     * Transition to a specific state by ID (used for AI handoff)
     */
    transitionTo(stateId: string): Message[] {
        this.currentStateId = stateId;
        const nextState = this.flow[stateId];
        if (!nextState) {
            console.error('[FlowEngine] State not found for transition:', stateId);
            return [];
        }

        const messages = this.processState(nextState);
        messages.forEach(msg => this.chatHistory.push(msg));
        return messages;
    }
}
